import React, { useCallback, useEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import type { ColDef, ColGroupDef, ValueFormatterParams, ValueGetterParams, ValueParserParams, CellClassParams, CellValueChangedEvent, CellClickedEvent, CellStyle } from 'ag-grid-community';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUpload, faPen } from '@fortawesome/free-solid-svg-icons';
import type { VarianceTableWidgetInstanceConfig, VarianceTableData, VarianceRecord } from './types';
import { useDataProviderWidget } from '../../../blocks/hooks';
import { WidgetLoadingState, WidgetErrorState, WidgetPreviewBanner } from '../../../design';
import { AgGridWrapper } from '../../../blocks/tables';
import Modal, { ModalHeader, ModalBody, ModalFooter } from '../../../../ui/Modal';
import Button from '../../../../ui/Button';
import { varianceOverrideService } from '../../../../../services/varianceOverrideService';
import { modelService } from '../../../../../services/modelService';
import { setSelectedKpi, getSelectedKpi, subscribeKpi, clearSelectedKpi } from '../../../blocks/hooks/kpiSelectionStore';

// Preview (no asset): a small tidy slice so the grid renders standalone.
const SAMPLE_DATA: VarianceTableData = {
  ticker: 'NVDA',
  variance_data: [
    { seq: 0, kpi: 'Total Revenue', kind: 'value', unit: '$mn', period: '3Q26', is_estimate: true, internal: 72119, consensus: 63294, variance: 0.139 },
    { seq: 0, kpi: 'Total Revenue', kind: 'value', unit: '$mn', period: 'FY26', is_estimate: true, internal: 276115, consensus: 254230, variance: 0.086 },
    { seq: 1, kpi: 'EBIT', kind: 'value', unit: '$mn', period: '3Q26', is_estimate: true, internal: 23597, consensus: 20715, variance: 0.139 },
    { seq: 1, kpi: 'EBIT', kind: 'value', unit: '$mn', period: 'FY26', is_estimate: true, internal: 96728, consensus: 89399, variance: 0.082 },
    { seq: 2, kpi: 'Diluted EPS', kind: 'pershare', unit: '$', period: '3Q26', is_estimate: true, internal: 8.05, consensus: 7.22, variance: 0.115 },
    { seq: 2, kpi: 'Diluted EPS', kind: 'pershare', unit: '$', period: 'FY26', is_estimate: true, internal: 35.63, consensus: 33.62, variance: 0.060 },
  ],
};

interface Props {
  config: VarianceTableWidgetInstanceConfig;
  dashboardData?: Record<string, unknown>;
  readOnly?: boolean;
  widgetInstanceId?: string;
}

const num = (v: unknown): number | null => {
  if (v == null || v === '') return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

const fmtValue = (v: unknown, kind?: string): string => {
  const n = num(v);
  if (n == null) return '';
  if (kind === 'pct') return `${(n * 100).toFixed(1)}%`;
  if (kind === 'pershare') return n.toFixed(2);
  return n.toLocaleString('en-US', { maximumFractionDigits: 0 });
};

const fmtVar = (v: unknown, kind?: string): string => {
  const n = num(v);
  if (n == null) return '';
  const sign = n > 0 ? '+' : '';
  return kind === 'pct' ? `${sign}${(n * 100).toFixed(1)}pp` : `${sign}${(n * 100).toFixed(1)}%`;
};

const ovKey = (kpi: string, period: string) => `${kpi}__${period}`;

const VarianceTableWidgetRenderer: React.FC<Props> = ({ config, dashboardData = {}, widgetInstanceId }) => {
  const uid = dashboardData?.assetDataUid as string | undefined;
  const isPreview = !uid;
  const editable = !isPreview;

  const { data, loading, error, refetch } = useDataProviderWidget<VarianceTableData>(
    'variance_table',
    uid,
    config as unknown as Record<string, unknown>,
    { enabled: !!uid, widgetInstanceId },
  );

  const displayData = uid ? data : SAMPLE_DATA;
  const ticker = displayData?.ticker || 'Asset';
  const records = useMemo<VarianceRecord[]>(() => displayData?.variance_data ?? [], [displayData]);

  // Analyst overrides for this ticker (their internal estimate per KPI/period).
  const [overrides, setOverrides] = useState<Map<string, number>>(new Map());
  useEffect(() => {
    if (!uid) { setOverrides(new Map()); return; }
    let active = true;
    varianceOverrideService.list(uid)
      .then((list) => { if (active) setOverrides(new Map(list.map((o) => [ovKey(o.kpi, o.period), o.internal_value]))); })
      .catch(() => { if (active) setOverrides(new Map()); });
    return () => { active = false; };
  }, [uid]);

  // Pivot tidy records into KPI rows x period column-groups, merging overrides
  // over the model's internal value (override wins).
  const { rowData, periods } = useMemo(() => {
    const periodOrder: Array<{ period: string; est: boolean }> = [];
    const seenP = new Set<string>();
    const kpiInfo = new Map<string, { kind: string; unit: string; seq: number }>();
    const cell = new Map<string, VarianceRecord>();
    for (const r of records) {
      const p = String(r.period);
      if (!seenP.has(p)) { seenP.add(p); periodOrder.push({ period: p, est: !!r.is_estimate }); }
      const k = String(r.kpi);
      if (!kpiInfo.has(k)) kpiInfo.set(k, { kind: String(r.kind), unit: String(r.unit), seq: Number(r.seq) });
      cell.set(`${k}|${p}`, r);
    }
    const kpis = [...kpiInfo.entries()].sort((a, b) => a[1].seq - b[1].seq);
    const rows = kpis.map(([kpi, info]) => {
      const row: Record<string, unknown> = { kpi, kind: info.kind, unit: info.unit };
      for (const { period } of periodOrder) {
        const rec = cell.get(`${kpi}|${period}`);
        const modelInt = rec ? num(rec.internal) : null;
        const ov = overrides.get(ovKey(kpi, period));
        row[`${period}__int`] = ov !== undefined ? ov : modelInt;
        row[`${period}__cons`] = rec ? num(rec.consensus) : null;
      }
      return row;
    });
    return { rowData: rows, periods: periodOrder };
  }, [records, overrides]);

  const onCellValueChanged = useCallback((e: CellValueChangedEvent) => {
    const field = e.colDef.field || '';
    if (!field.endsWith('__int') || !uid) return;
    const period = field.slice(0, -'__int'.length);
    const kpi = String((e.data as Record<string, unknown>).kpi);
    const v = num(e.newValue);
    setOverrides((prev) => {
      const next = new Map(prev);
      if (v == null) next.delete(ovKey(kpi, period)); else next.set(ovKey(kpi, period), v);
      return next;
    });
    varianceOverrideService.upsert(uid, kpi, period, v).catch(() => {});
  }, [uid]);

  // Cross-widget: clicking a KPI here drives the Consensus Revision chart below.
  const selectedKpi = useSyncExternalStore(subscribeKpi, () => getSelectedKpi(uid));
  const onCellClicked = useCallback((e: CellClickedEvent) => {
    if (!uid || e.colDef.field !== 'kpi') return;
    const data = e.data as Record<string, unknown>;
    const kpi = String(data?.kpi ?? '');
    // Margins (pct) have no consensus-revision series — only value/per-share KPIs.
    if (!kpi || String(data?.kind ?? '') === 'pct') return;
    setSelectedKpi(uid, kpi);
  }, [uid]);
  // Reset the KPI selection when the asset changes, so each asset starts fresh.
  useEffect(() => () => { clearSelectedKpi(uid); }, [uid]);

  // "Edit estimates" modal: edit every KPI x period at once, then save.
  const [editOpen, setEditOpen] = useState(false);
  const [draft, setDraft] = useState<Map<string, string>>(new Map());
  // Snapshot of what the modal opened with — only cells the analyst actually
  // CHANGED get saved. Persisting the untouched consensus pre-fills would
  // register an "estimate" equal to the street on every KPI (phantom 0.0%
  // overrides that light up downstream overlays like the Consensus Revision
  // chart's "Your Estimate" line).
  const [draftInitial, setDraftInitial] = useState<Map<string, string>>(new Map());
  const openEdit = useCallback(() => {
    const d = new Map<string, string>();
    for (const row of rowData) {
      const kpi = String(row.kpi);
      const kind = String(row.kind);
      for (const { period } of periods) {
        const v = num(row[`${period}__int`]);
        // Seed $mn values rounded — the raw feed's decimals (e.g. 47935.59)
        // read as noise in an input; the grid displays whole $mn anyway.
        // Dirty-tracking compares against this same seeded string, so an
        // untouched rounded pre-fill is still never saved as an estimate.
        const seed = v == null ? '' : kind === 'pershare' ? v.toFixed(2) : kind === 'value' ? String(Math.round(v)) : String(v);
        d.set(ovKey(kpi, period), seed);
      }
    }
    setDraft(d);
    setDraftInitial(new Map(d));
    setEditOpen(true);
  }, [rowData, periods]);
  const handleSaveDraft = useCallback(() => {
    if (!uid) { setEditOpen(false); return; }
    const next = new Map(overrides);
    for (const row of rowData) {
      const kpi = String(row.kpi);
      for (const { period } of periods) {
        const k = ovKey(kpi, period);
        const raw = draft.get(k);
        if (raw === undefined || raw === draftInitial.get(k)) continue; // untouched
        const v = raw.trim() === '' ? null : num(raw.replace(/[,$%\s]/g, ''));
        if (v == null) next.delete(k); else next.set(k, v);
        varianceOverrideService.upsert(uid, kpi, period, v).catch(() => {});
      }
    }
    setOverrides(next);
    setEditOpen(false);
  }, [uid, overrides, rowData, periods, draft, draftInitial]);

  // Upload a tweaked InSync Excel model for this asset, then re-ingest.
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const handleUploadFile = useCallback(async (f: File | undefined) => {
    if (!f || !uid) return;
    setUploading(true);
    try { await modelService.upload(uid, f); await refetch?.(); }
    catch { /* next fetch surfaces the error state */ }
    finally { setUploading(false); }
  }, [uid, refetch]);
  const renderUpload = () => (uid ? (
    <>
      <input
        ref={fileInputRef} type="file" accept=".xlsx" className="hidden"
        onChange={(e) => { handleUploadFile(e.target.files?.[0]); e.target.value = ''; }}
      />
      <button
        type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading}
        title="Upload tweaked InSync model (.xlsx)" aria-label="Upload model"
        className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 px-2 py-1 text-[10px] font-semibold text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800 disabled:opacity-50"
      >
        <FontAwesomeIcon icon={faUpload} className="text-[10px]" />
        {uploading ? 'Uploading…' : 'Upload model'}
      </button>
    </>
  ) : null);

  const columnDefs = useMemo<(ColDef | ColGroupDef)[]>(() => {
    const kindOf = (p: { data?: unknown }) => String((p.data as Record<string, unknown>)?.kind ?? '');
    const cols: (ColDef | ColGroupDef)[] = [
      {
        headerName: 'KPI', field: 'kpi', width: 150, minWidth: 110, pinned: 'left', cellClass: 'font-medium',
        // Click a KPI to plot its consensus revision in the chart below.
        cellStyle: (p: CellClassParams): CellStyle | null => {
          const data = p.data as Record<string, unknown>;
          if (String(data?.kind ?? '') === 'pct') return null; // margins aren't selectable
          const style: CellStyle = { cursor: 'pointer' };
          if (String(data?.kpi ?? '') === selectedKpi) {
            style.fontWeight = 700;
            style.boxShadow = 'inset 3px 0 0 #2563eb';
            style.background = 'rgba(37,99,235,0.06)';
          }
          return style;
        },
      },
    ];
    for (const { period, est } of periods) {
      const intField = `${period}__int`;
      const consField = `${period}__cons`;
      cols.push({
        headerName: `${period}${est ? 'E' : 'A'}`,
        headerClass: est ? 'text-primary-600 ag-center-header' : 'ag-center-header',
        children: [
          {
            headerName: 'Yours', headerTooltip: 'Your estimate — click a cell to enter it',
            field: intField, flex: 1, minWidth: 76, type: 'rightAligned', cellClass: 'tabular-nums',
            editable: (p) => editable && (kindOf(p) === 'value' || kindOf(p) === 'pershare'),
            valueParser: (p: ValueParserParams) => {
              const raw = String(p.newValue ?? '').replace(/[,$%\s]/g, '').trim();
              if (raw === '') return null;
              const n = Number(raw);
              return Number.isFinite(n) ? n : p.oldValue;
            },
            valueFormatter: (p: ValueFormatterParams) => fmtValue(p.value, kindOf(p)),
            cellStyle: (p: CellClassParams) =>
              overrides.has(ovKey(String((p.data as Record<string, unknown>)?.kpi ?? ''), period))
                ? { fontWeight: 600, boxShadow: 'inset 2px 0 0 #2563eb' }
                : null,
          },
          { headerName: 'Street', headerTooltip: 'Street consensus (InSync)',
            field: consField, flex: 1, minWidth: 76, type: 'rightAligned', cellClass: 'tabular-nums',
            valueFormatter: (p: ValueFormatterParams) => fmtValue(p.value, kindOf(p)) },
          { headerName: 'Var', headerTooltip: 'Your variance vs the street — % for values, pp for margins',
            flex: 1, minWidth: 64, type: 'rightAligned', cellClass: 'tabular-nums',
            valueGetter: (p: ValueGetterParams) => {
              const int = num((p.data as Record<string, unknown>)?.[intField]);
              const cons = num((p.data as Record<string, unknown>)?.[consField]);
              if (int == null || cons == null || cons === 0) return null;
              return kindOf(p) === 'pct' ? int - cons : int / cons - 1;
            },
            valueFormatter: (p: ValueFormatterParams) => fmtVar(p.value, kindOf(p)),
            cellStyle: (p: CellClassParams) => {
              const n = num(p.value);
              if (n == null || Math.abs(n) < 1e-9) return null;
              return { color: n > 0 ? '#16a34a' : '#dc2626', fontWeight: 600 };
            },
          },
        ],
      });
    }
    return cols;
  }, [periods, overrides, editable, selectedKpi]);

  const defaultColDef = useMemo<ColDef>(() => ({ sortable: false, filter: false, resizable: true }), []);

  if (loading) return <WidgetLoadingState size="sm" />;
  if (error) return <WidgetErrorState message={error} onRetry={refetch} />;
  if (uid && !rowData.length) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3 p-6 text-center rounded-xl border border-neutral-200 bg-surface">
        <div>
          <p className="text-sm font-semibold text-neutral-700">No internal model on file</p>
          <p className="mt-1 text-xs text-neutral-500 max-w-xs">
            Variance needs {ticker}&apos;s tweaked InSync model (Model Inputs vs consensus). Upload it to light this up.
          </p>
        </div>
        {renderUpload()}
      </div>
    );
  }
  if (!rowData.length) return null;

  return (
    <div className={`h-full flex flex-col overflow-hidden rounded-xl border border-neutral-200 bg-surface shadow-sm ${isPreview ? 'opacity-70' : ''}`}>
      {isPreview && <WidgetPreviewBanner />}
      <div className="flex items-center gap-3 px-3 py-2 border-b border-neutral-100 shrink-0">
        <span className="text-sm font-semibold text-neutral-800">Variance vs Street</span>
        <span className="text-[11px] text-neutral-400 uppercase tracking-wide">{ticker} · your estimates vs street consensus</span>
        <div className="ml-auto flex items-center gap-2">
          <span className="flex items-center gap-2 text-[10px] text-neutral-400">
            <span><span className="text-success-600 font-semibold">+</span> above</span>
            <span><span className="text-danger-600 font-semibold">−</span> below</span>
          </span>
          {editable && (
            <button
              type="button" onClick={openEdit}
              title="Edit your estimates" aria-label="Edit estimates"
              className="inline-flex items-center gap-1 rounded-lg border border-neutral-200 px-2 py-1 text-[10px] font-semibold text-neutral-500 hover:bg-neutral-100 hover:text-neutral-800"
            >
              <FontAwesomeIcon icon={faPen} className="text-[10px]" />
              Edit
            </button>
          )}
          {renderUpload()}
        </div>
      </div>
      <div className="flex-1 overflow-hidden">
        <AgGridWrapper
          containerClassName="ag-theme-alpine ag-compact h-full w-full"
          columnDefs={columnDefs}
          rowData={rowData}
          defaultColDef={defaultColDef}
          getRowId={(p) => String((p.data as Record<string, unknown>).kpi)}
          onCellValueChanged={onCellValueChanged}
          onCellClicked={onCellClicked}
          singleClickEdit
          stopEditingWhenCellsLoseFocus
          rowHeight={30}
          headerHeight={28}
          groupHeaderHeight={26}
          suppressCellFocus={false}
        />
      </div>
      <div className="shrink-0 px-3 pb-1.5 pt-0.5 flex items-center justify-between">
        <span className="text-[9px] uppercase tracking-wide text-neutral-400">
          {editable ? 'Click a cell under “Yours” — or the Edit button — to enter your estimate · ' : ''}USD Mn except per-share · margins in pp
        </span>
        <span className="text-[9px] uppercase tracking-wide text-neutral-400">Source: InSync model vs consensus</span>
      </div>

      <Modal isOpen={editOpen} onClose={() => setEditOpen(false)} size="full">
        <ModalHeader>Edit estimates — {ticker}</ModalHeader>
        <ModalBody>
          <p className="text-sm text-neutral-500 mb-3">
            Enter your internal estimate for each KPI and period. Consensus is shown for reference; the variance updates as you type and is saved when you click Save.
          </p>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr className="text-[11px] uppercase tracking-wide text-neutral-400">
                  <th className="py-1 pr-3 text-left font-medium">KPI</th>
                  {periods.map(({ period, est }) => (
                    <th key={period} className="px-1.5 py-1 text-right font-medium">{period}{est ? 'E' : 'A'}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rowData.map((row) => {
                  const kpi = String(row.kpi);
                  const kind = String(row.kind);
                  return (
                    <tr key={kpi} className="border-t border-neutral-100">
                      <td className="py-1.5 pr-3 font-medium text-neutral-800 whitespace-nowrap">{kpi}</td>
                      {periods.map(({ period }) => {
                        const k = ovKey(kpi, period);
                        const cons = num(row[`${period}__cons`]);
                        const raw = draft.get(k) ?? '';
                        const iv = raw.trim() === '' ? null : num(raw.replace(/[,$%\s]/g, ''));
                        let varStr = '';
                        let varColor = '';
                        if (iv != null && cons != null && cons !== 0) {
                          const vv = kind === 'pct' ? iv - cons : iv / cons - 1;
                          if (Math.abs(vv) >= 1e-9) { varStr = fmtVar(vv, kind); varColor = vv > 0 ? 'text-success-600' : 'text-danger-600'; }
                        }
                        return (
                          <td key={period} className="px-1.5 py-1.5 align-top">
                            <input
                              type="text"
                              inputMode="decimal"
                              value={raw}
                              onChange={(e) => setDraft((prev) => { const n = new Map(prev); n.set(k, e.target.value); return n; })}
                              className="w-24 rounded-md border border-neutral-300 bg-surface px-2 py-1 text-right text-sm tabular-nums focus:border-primary-400 focus:outline-none focus:ring-1 focus:ring-primary-400"
                            />
                            <div className="mt-0.5 flex items-center justify-between gap-1 text-[10px] text-neutral-400">
                              <span>street {fmtValue(cons, kind)}</span>
                              {varStr && <span className={`font-semibold ${varColor}`}>{varStr}</span>}
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" size="sm" onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="primary" size="sm" onClick={handleSaveDraft}>Save estimates</Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default VarianceTableWidgetRenderer;
